import { isDatePartField } from './kqlBuilder';

/**
 * Accept and reject rules in words and rules (KPR-482; keepr-api docs/SCHEMA.md
 * § Accept and reject rules). The API judges every one of these again; they
 * exist so a manager hears "no" before Save, never instead of the server.
 */

// keepr-api utils/formReviewRules.js MAX_RULES / CAP_BOUNDS / MAX_RULE_NAME.
export const MAX_RULES = 20;
export const MAX_RULE_NAME = 100;
export const MAX_REASON = 500;
export const CAP_BOUNDS = Object.freeze([1, 2000, 50]);

// The plain-words confirmation the API asks for an accept rule on a
// collection anyone can read (guardrail 1, the owner's ruling f06).
export const PUBLISHING_WORDS = 'Anyone can read this collection, so a submission an accept rule matches will be public at once, without anyone reading it first.';

let draftSeq = 0;
/** A new rule as the editor holds it: no id until the API gives it one. */
export const blankRule = (decision = 'reject') => {
	draftSeq += 1;
	return { key: `new-${draftSeq}`, id: null, name: '', when: '', decision, reason: '', broken: null };
};

/** The stored rules as the editor holds them, each with a stable React key. */
export const draftsFrom = review => (Array.isArray(review?.rules) ? review.rules : []).map(rule => ({
	key: rule.id,
	id: rule.id,
	name: rule.name || '',
	when: rule.when || '',
	decision: rule.decision === 'accept' ? 'accept' : 'reject',
	reason: rule.reason || '',
	broken: rule.broken || null,
}));

/**
 * The names a rule may read on this form — what it ASKS (its public `fields`)
 * and what it SHOWS (`shownFields`), never a private question or a prefilled
 * element. `definition` is a public form's `definition`, or an intake link's
 * row (the same keys at its top level).
 */
export function readableNames(definition) {
	const d = definition || {};
	const privateNames = new Set(Array.isArray(d.privateFields) ? d.privateFields : []);
	const prefilled = new Set(Object.keys(d.prefill && typeof d.prefill === 'object' ? d.prefill : {}));
	const usable = name => typeof name === 'string' && name && !privateNames.has(name) && !prefilled.has(name);
	return new Set([
		...(Array.isArray(d.fields) ? d.fields : []).filter(usable),
		...(Array.isArray(d.shownFields) ? d.shownFields : []).filter(usable),
	]);
}

/**
 * The filter builder's fields, narrowed to what a rule may read: the form's
 * own answers, and the parts (weekday, hour, month) of its date answers.
 * Never a system field, a facet, another record (a hop), a set or a card.
 */
export function ruleFilterFields(fields, names) {
	return (fields || []).filter((field) => {
		if (!field || field.system || field.facet || field.hop) return false;
		const path = Array.isArray(field.path) ? field.path : [];
		if (path.length === 1) return names.has(path[0]);
		return path.length === 2 && isDatePartField(field) && names.has(path[0]);
	});
}

/** Why this list cannot be saved yet, in words, or null. */
export function rulesProblem(rules, cap, perDay = null) {
	if ((rules || []).length > MAX_RULES) return `A form has at most ${MAX_RULES} rules.`;
	for (let i = 0; i < (rules || []).length; i++) {
		const rule = rules[i];
		if (!`${rule.name || ''}`.trim()) return `Rule ${i + 1} needs a name — it is what History says decided a submission.`;
		if (`${rule.name}`.trim().length > MAX_RULE_NAME) return `Rule ${i + 1}’s name is at most ${MAX_RULE_NAME} characters.`;
		if (!`${rule.when || ''}`.trim()) return `Rule ${i + 1} needs a condition: the answers it ${rule.decision === 'accept' ? 'accepts' : 'rejects'}.`;
	}
	const [min, max] = CAP_BOUNDS;
	const ceiling = Number.isInteger(perDay) ? Math.min(max, perDay) : max;
	if (!Number.isInteger(cap) || cap < min || cap > ceiling) return `Accepted by rule a day must be a whole number from ${min} to ${ceiling.toLocaleString('en-US')}.`;
	return null;
}

/** The API's refusal of a save or a preview, in words. `index` the rule it names, when it names one. */
export function describeRulesError(error) {
	const data = error?.response?.data || {};
	const index = Number.isInteger(data.rule) ? data.rule : null;
	switch (data.code) {
		case 'public_forms_off':
			return { message: 'Rules on public forms are not switched on for this keepr server yet.', index };
		case 'publishing_not_acknowledged':
			return { message: `${PUBLISHING_WORDS} Tick the box to say you know, then save again.`, index, needsAcknowledgement: true };
		case 'form_retired':
			return { message: 'This form has been retired.', index };
		case 'link_revoked':
		case 'link_expired':
			return { message: 'This link has ended, so its rules can no longer change.', index };
		case 'card_not_member':
			return { message: 'The card this form collects is no longer in this collection.', index };
		default:
			return { message: data.message || 'The rules could not be saved.', index };
	}
}

/**
 * A preview section in words: "these rules would accept 2, reject 1 and leave
 * 3 for a person". Said as what the rules WOULD do — never "2 accepted",
 * which reads as something that happened.
 */
export function previewSentence(section) {
	if (!section || !section.total) return 'nothing to judge';
	const parts = [];
	if (section.accept) parts.push(`accept ${section.accept}`);
	if (section.reject) parts.push(`reject ${section.reject}`);
	if (section.none) parts.push(`leave ${section.none} for a person`);
	const list = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0];
	return `these rules would ${list}`;
}

/** The item a rule accepted and nobody read (keepr-api `intake.decidedBy: 'rule'`). */
export const isRuleAccepted = item => item?.intake?.reviewState === 'accepted' && item?.intake?.decidedBy === 'rule';

/** How many rules a review holds, and whether any stopped working. */
export function reviewSummary(review) {
	const rules = Array.isArray(review?.rules) ? review.rules : [];
	return { count: rules.length, broken: rules.filter(rule => rule?.broken).length };
}
